import { useEffect } from "react";

interface JsonLdProps {
  data: Record<string, unknown>;
}

export const JsonLd = ({ data }: JsonLdProps) => {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(data);
    script.id = `jsonld-${JSON.stringify(data["@type"])}`;
    
    // Remove existing script with same ID
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      script.remove();
    };
  }, [data]);
  
  return null;
};

// Pre-built JSON-LD schemas for Ritual
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Ritual",
  "url": "https://ritual.app",
  "logo": "https://ritual.app/ritual-logo.png",
  "description": "Agent-native operations platform for SMB teams. Turn processes into rituals that run themselves.",
  "sameAs": [
    "https://twitter.com/RitualOps"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "support@ritual.app",
    "contactType": "customer support"
  }
};

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Ritual",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web, iOS, Android",
  "description": "Swipe-to-complete task management for operations teams. Transform SOPs into actionable task cards.",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Free tier available"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "ratingCount": "127"
  },
  "featureList": [
    "Swipe-to-complete tasks",
    "Photo proof capture",
    "AI-powered SOP generation",
    "Real-time team visibility",
    "Mobile-first design"
  ]
};

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Ritual?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ritual is an agent-native operations platform that transforms SOPs into swipeable task cards. Teams complete tasks by swiping right, with optional photo/file proof capture."
      }
    },
    {
      "@type": "Question",
      "name": "How does swipe-to-complete work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tasks appear as cards in your feed. Swipe right to complete, left to skip. If proof is required, a capture sheet appears for photos, files, or notes."
      }
    },
    {
      "@type": "Question",
      "name": "Is Ritual free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! Ritual offers a free tier for teams up to 5 users. Paid plans start at ₹5,000/month for larger teams and advanced features."
      }
    },
    {
      "@type": "Question",
      "name": "Can I use Ritual on mobile?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. Ritual is mobile-first with PWA support. Install it on your home screen for a native app experience."
      }
    }
  ]
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});
